/**
 * Page Race Visualization Component
 *
 * Shows a "race" between three pages:
 * - Your Original Page
 * - Your Enhanced Page (with AEO)
 * - SERP Competitor
 *
 * Animated to show how AEO elements help the page compete.
 */

import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Trophy, TrendingUp, TrendingDown, Minus, Globe, Sparkles, FileText } from 'lucide-react';
import { cn } from '@/lib/utils';
import { MetricsSet, CompetitorMetrics } from '@/services/aeoDemoService';

interface PageRaceVizProps {
  baseline: MetricsSet | null;
  enhanced: MetricsSet | null;
  competitor: CompetitorMetrics | null;
  isCalculating?: boolean;
}

interface RacerData {
  id: string;
  name: string;
  icon: React.ReactNode;
  score: number;
  color: string;
  isLeader: boolean;
  position: number;
  distanceFromLeader: number;
}

const TRACK_WIDTH = 100; // Percentage width
const MIN_SCORE = 0;
const MAX_SCORE = 100;

function RaceTrack({ racers }: { racers: RacerData[] }) {
  // Sort by score descending to determine positions
  const sortedRacers = [...racers].sort((a, b) => b.score - a.score);
  const leaderScore = sortedRacers[0]?.score || 0;

  return (
    <div className="relative bg-muted/30 rounded-lg p-4 overflow-hidden">
      {/* Finish line */}
      <div className="absolute right-4 top-0 bottom-0 w-1 bg-gradient-to-b from-amber-400 via-amber-500 to-amber-400 opacity-50" />
      <div className="absolute right-2 top-2 text-xs font-medium text-amber-600">
        FINISH
      </div>

      {/* Distance markers */}
      <div className="absolute inset-x-4 top-0 flex justify-between text-xs text-muted-foreground pt-1">
        {[0, 25, 50, 75, 100].map(mark => (
          <span key={mark} className="opacity-50">{mark}</span>
        ))}
      </div>

      {/* Race lanes */}
      <div className="space-y-4 mt-6">
        {sortedRacers.map((racer, index) => (
          <RaceLane
            key={racer.id}
            racer={racer}
            position={index + 1}
            leaderScore={leaderScore}
          />
        ))}
      </div>
    </div>
  );
}

function RaceLane({
  racer,
  position,
  leaderScore,
}: {
  racer: RacerData;
  position: number;
  leaderScore: number;
}) {
  const positionPercent = ((racer.score - MIN_SCORE) / (MAX_SCORE - MIN_SCORE)) * (TRACK_WIDTH - 15);
  const distanceBehind = leaderScore - racer.score;

  return (
    <div className="relative">
      {/* Lane background */}
      <div className="h-12 bg-muted/50 rounded-md relative overflow-hidden">
        {/* Progress track */}
        <div
          className="absolute inset-y-0 left-0 rounded-md transition-all duration-500 ease-out"
          style={{
            width: `${positionPercent + 8}%`,
            backgroundColor: `${racer.color}20`,
          }}
        />

        {/* Racer icon */}
        <div
          className="absolute top-1/2 -translate-y-1/2 transition-all duration-500 ease-out flex items-center gap-2"
          style={{ left: `${positionPercent}%` }}
        >
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center shadow-lg border-2 border-white"
            style={{ backgroundColor: racer.color }}
          >
            {racer.icon}
          </div>
        </div>

        {/* Position badge */}
        <div className="absolute left-2 top-1/2 -translate-y-1/2">
          <Badge
            variant={position === 1 ? "default" : "outline"}
            className={cn(
              "text-xs font-bold",
              position === 1 && "bg-amber-500 hover:bg-amber-500"
            )}
          >
            #{position}
          </Badge>
        </div>
      </div>

      {/* Racer info below lane */}
      <div className="flex items-center justify-between mt-1 px-2">
        <span className="text-sm font-medium truncate max-w-[150px]">{racer.name}</span>
        <div className="flex items-center gap-2">
          <span className="text-sm font-bold" style={{ color: racer.color }}>
            {Math.round(racer.score)}
          </span>
          {position > 1 && distanceBehind > 0 && (
            <span className="text-xs text-muted-foreground">
              (-{Math.round(distanceBehind)})
            </span>
          )}
          {position === 1 && (
            <Trophy className="w-4 h-4 text-amber-500" />
          )}
        </div>
      </div>
    </div>
  );
}

function ScoreSummary({
  racers,
  enhanced,
  baseline,
}: {
  racers: RacerData[];
  enhanced: MetricsSet | null;
  baseline: MetricsSet | null;
}) {
  const sortedRacers = [...racers].sort((a, b) => b.score - a.score);
  const enhancedRacer = sortedRacers.find(r => r.id === 'enhanced');
  const baselineRacer = sortedRacers.find(r => r.id === 'baseline');
  const competitorRacer = sortedRacers.find(r => r.id === 'competitor');

  const enhancedPosition = sortedRacers.findIndex(r => r.id === 'enhanced') + 1;
  const baselinePosition = sortedRacers.findIndex(r => r.id === 'baseline') + 1;

  const improvement = enhanced && baseline
    ? Math.round(enhanced.overall.total - baseline.overall.total)
    : 0;

  const beatsCompetitor = enhancedRacer && competitorRacer && enhancedRacer.score > competitorRacer.score;
  const tiedWithCompetitor = enhancedRacer && competitorRacer && Math.abs(enhancedRacer.score - competitorRacer.score) < 1;

  return (
    <div className="mt-4 grid grid-cols-3 gap-3">
      {/* Position change */}
      <div className="text-center p-3 rounded-lg bg-muted/30">
        <div className="text-xs text-muted-foreground mb-1">Position Change</div>
        <div className="flex items-center justify-center gap-1">
          <span className="text-lg font-semibold text-muted-foreground">
            #{baselinePosition}
          </span>
          <span className="text-muted-foreground">→</span>
          <span className={cn(
            "text-lg font-semibold",
            enhancedPosition === 1 ? "text-amber-500" :
            enhancedPosition < baselinePosition ? "text-green-600" :
            "text-muted-foreground"
          )}>
            #{enhancedPosition}
          </span>
        </div>
      </div>

      {/* Score improvement */}
      <div className="text-center p-3 rounded-lg bg-muted/30">
        <div className="text-xs text-muted-foreground mb-1">Score Change</div>
        <div className="flex items-center justify-center gap-1">
          {improvement > 0 ? (
            <>
              <TrendingUp className="w-4 h-4 text-green-600" />
              <span className="text-lg font-semibold text-green-600">+{improvement}</span>
            </>
          ) : improvement < 0 ? (
            <>
              <TrendingDown className="w-4 h-4 text-red-600" />
              <span className="text-lg font-semibold text-red-600">{improvement}</span>
            </>
          ) : (
            <>
              <Minus className="w-4 h-4 text-muted-foreground" />
              <span className="text-lg font-semibold text-muted-foreground">0</span>
            </>
          )}
        </div>
      </div>

      {/* Competitor status */}
      <div className="text-center p-3 rounded-lg bg-muted/30">
        <div className="text-xs text-muted-foreground mb-1">vs Competitor</div>
        <div className="text-lg font-semibold">
          {beatsCompetitor ? (
            <span className="text-green-600">Ahead!</span>
          ) : tiedWithCompetitor ? (
            <span className="text-amber-500">Tied</span>
          ) : enhanced ? (
            <span className="text-red-500">Behind</span>
          ) : (
            <span className="text-muted-foreground">-</span>
          )}
        </div>
      </div>
    </div>
  );
}

export function PageRaceViz({
  baseline,
  enhanced,
  competitor,
  isCalculating,
}: PageRaceVizProps) {
  const racers = useMemo((): RacerData[] => {
    const result: RacerData[] = [];

    // Baseline (Your Original Page)
    if (baseline) {
      result.push({
        id: 'baseline',
        name: 'Your Page',
        icon: <FileText className="w-5 h-5 text-white" />,
        score: baseline.overall.total,
        color: '#6b7280', // gray
        isLeader: false,
        position: 0,
        distanceFromLeader: 0,
      });
    }

    // Enhanced (Your AEO Page)
    if (enhanced) {
      result.push({
        id: 'enhanced',
        name: 'With AEO',
        icon: <Sparkles className="w-5 h-5 text-white" />,
        score: enhanced.overall.total,
        color: '#3b82f6', // blue
        isLeader: false,
        position: 0,
        distanceFromLeader: 0,
      });
    } else if (baseline) {
      // Show baseline as enhanced if no enhancement selected
      result.push({
        id: 'enhanced',
        name: 'With AEO',
        icon: <Sparkles className="w-5 h-5 text-white" />,
        score: baseline.overall.total,
        color: '#3b82f6',
        isLeader: false,
        position: 0,
        distanceFromLeader: 0,
      });
    }

    // Competitor
    if (competitor) {
      result.push({
        id: 'competitor',
        name: competitor.name || 'SERP Leader',
        icon: <Globe className="w-5 h-5 text-white" />,
        score: competitor.metrics.overall.total,
        color: '#ef4444', // red
        isLeader: false,
        position: 0,
        distanceFromLeader: 0,
      });
    }

    // Calculate positions and distances
    result.sort((a, b) => b.score - a.score);
    const leaderScore = result[0]?.score || 0;

    result.forEach((racer, index) => {
      racer.position = index + 1;
      racer.isLeader = index === 0;
      racer.distanceFromLeader = leaderScore - racer.score;
    });

    return result;
  }, [baseline, enhanced, competitor]);

  if (!baseline) {
    return (
      <Card>
        <CardContent className="p-6 text-center text-muted-foreground">
          No baseline metrics available
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Trophy className="w-5 h-5 text-amber-500" />
            Page Race
          </CardTitle>
          {isCalculating && (
            <Badge variant="outline" className="animate-pulse">
              Calculating...
            </Badge>
          )}
        </div>
        <p className="text-sm text-muted-foreground">
          See how your page competes against the SERP leader
        </p>
      </CardHeader>
      <CardContent>
        <RaceTrack racers={racers} />
        <ScoreSummary racers={racers} enhanced={enhanced} baseline={baseline} />

        {competitor && (
          <div className="mt-4 text-center text-xs text-muted-foreground">
            Competitor: {competitor.name}
            {competitor.url && (
              <span className="ml-1">({competitor.domain})</span>
            )}
            {competitor.source === 'synthetic' && (
              <span className="ml-1">(industry benchmark)</span>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default PageRaceViz;
